import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import UntypedModal from '../Modal';
import DatepickerWrapper from '../Datepicker/DatepickerWrapper';
import styles from './GenerateGraphModal.module.scss';

const Modal = UntypedModal as unknown as React.FC<{
  isVisible: boolean;
  title: string;
  content: React.ReactNode;
  footer: React.ReactNode;
  onClose: () => void;
  className?: string;
  doNotCloseOnOutsideClick?: boolean;
}>;

const Datepicker = DatepickerWrapper as unknown as React.FC<{
  onChange: (start: Date | null, end: Date | null) => void;
  initStartDate: Date | null;
  initEndDate: Date | null;
  isRange: boolean;
  buttonDateFormat?: string;
  className?: string;
  emptyLabel?: string;
  emptyLabelActive?: boolean;
  clearable?: boolean;
  allTimePreset?: boolean;
}>;

export interface GenerateGraphValues {
  name: string;
  startDate: Date | null;
  endDate: Date | null;
}

interface GenerateGraphModalProps {
  onClose: () => void;
  // regenerate keeps the same form but relabels the submit button
  mode: 'generate' | 'regenerate';
  // actor-kind graphs only — case/topic/threat graphs have no analysis window
  showDateRange: boolean;
  initialName?: string;
  initialStartDate?: Date | null;
  initialEndDate?: Date | null;
  isSubmitting?: boolean;
  onSubmit: (values: GenerateGraphValues) => void;
}

// backend GraphName limit (PR #821)
const NAME_MAX_LENGTH = 200;

// Mount only while open (initial values are read once into state) — same
// conditional-mount contract as AddToSourceGroupModal.
export default function GenerateGraphModal({
  onClose,
  mode,
  showDateRange,
  initialName,
  initialStartDate,
  initialEndDate,
  isSubmitting = false,
  onSubmit,
}: GenerateGraphModalProps) {
  const { t } = useTranslation();
  // clamp the prefilled value too — maxLength only limits typing, not initial state
  const [name, setName] = useState((initialName ?? '').slice(0, NAME_MAX_LENGTH));
  // the required-field error shows only after the user leaves the empty field
  // (blur), same as the case form's onBlur validation
  const [nameTouched, setNameTouched] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(initialStartDate ?? null);
  const [endDate, setEndDate] = useState<Date | null>(initialEndDate ?? null);

  // name is required — the host page prefills it with the source entity's name,
  // so an empty field means the user actively cleared it
  const trimmedName = name.trim();
  const canSubmit = trimmedName.length > 0 && !isSubmitting;
  const showNameError = nameTouched && trimmedName.length === 0;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({ name: trimmedName, startDate, endDate });
  };

  const content = (
    <div className={styles.body}>
      <div className={styles.field}>
        <label htmlFor="generate-graph-name" className={styles.label}>
          {/* plain appended asterisk — same required-label pattern as the case form */}
          {t('Name')}*
        </label>
        <input
          id="generate-graph-name"
          type="text"
          className={`${styles.input} ${showNameError ? styles.inputError : ''}`}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => setNameTouched(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSubmit();
          }}
          placeholder={t('Enter graph name')}
          maxLength={NAME_MAX_LENGTH}
          required
          aria-invalid={showNameError}
          aria-describedby={showNameError ? 'generate-graph-name-error' : undefined}
          autoFocus
        />
        {showNameError && (
          <p id="generate-graph-name-error" className={styles.errorMessage} role="alert">
            {t('This field is required')}
          </p>
        )}
      </div>

      {showDateRange && (
        <div className={styles.field}>
          <span className={styles.label} id="generate-graph-date-range-label">
            {t('Date range (optional)')}
          </span>
          <div aria-labelledby="generate-graph-date-range-label">
            <Datepicker
              isRange
              clearable
              allTimePreset
              emptyLabel={t('All time')}
              emptyLabelActive
              buttonDateFormat="dd/MM/yy"
              initStartDate={startDate}
              initEndDate={endDate}
              onChange={(start, end) => {
                setStartDate(start);
                setEndDate(end);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );

  const footer = (
    <div className={styles.footer}>
      <button
        type="button"
        className="btn-primary"
        onClick={handleSubmit}
        disabled={!canSubmit}
      >
        {mode === 'regenerate' ? t('Regenerate') : t('Generate')}
      </button>
      <button
        type="button"
        className="btn-reset"
        onClick={onClose}
        disabled={isSubmitting}
      >
        {t('Cancel')}
      </button>
    </div>
  );

  return (
    <Modal
      isVisible
      title={t('Generate Graph')}
      content={content}
      footer={footer}
      onClose={onClose}
      doNotCloseOnOutsideClick
      className={styles.modal}
    />
  );
}
